import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, Timestamp, collection, query, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Period {
  periodId: string;
  periodNumber: string;
  order: number;
  subject: string;
  teacherId: string;
  teacherName: string;
  startTime: string;
  endTime: string;
  duration: number;
  isBreak: boolean;
  type: string;
}

interface DayTimetable {
  academicYear: string;
  classId: string;
  className: string;
  day: string;
  date: string;
  periods: Period[];
  createdAt: Timestamp;
  createdBy: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

interface Teacher {
  teacherId: string;
  name: string;
  subject?: string;
  department?: string;
}

interface ModifyTimeTableProps {
  domain: string;
}

const ModifyTimeTable: React.FC<ModifyTimeTableProps> = ({ domain }) => {
  const [classId, setClassId] = useState('');
  const [section, setSection] = useState('');
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [timetable, setTimetable] = useState<DayTimetable | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedPeriods, setEditedPeriods] = useState<Period[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>([]);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Class options (1-12)
  const classes = Array.from({ length: 12 }, (_, i) => (i + 1).toString());

  // Section options (A-J)
  const sections = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

  // Load teachers on component mount
  useEffect(() => {
    loadTeachers();
  }, [domain]);

  const loadTeachers = async () => {
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const q = query(teachersRef);
      const snapshot = await getDocs(q);

      const teachersList: Teacher[] = [];
      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        // Use teacherId from data, or fallback to document ID
        teachersList.push({
          teacherId: data.teacherId || docSnapshot.id || '',
          name: data.name || '',
          subject: data.subject || '',
          department: data.department || '',
        });
      });

      teachersList.sort((a, b) => a.name.localeCompare(b.name));
      setTeachers(teachersList);
      console.log('✅ Loaded teachers:', teachersList.length, teachersList.map(t => ({ id: t.teacherId, name: t.name })));
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  // Helper function to calculate duration in minutes
  const calculateDuration = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    return (endHour * 60 + endMin) - (startHour * 60 + startMin);
  };

  // Function to update teacher schedules when timetable is modified
  const updateTeacherSchedules = async (
    updatedPeriods: Period[],
    day: string,
    timetableClassId: string,
    timetableClassName: string,
    timetableDate: string
  ) => {
    try {
      console.log('🔄 Updating teacher schedules for day:', day);

      // Get all unique teacher IDs from the updated periods (excluding breaks)
      const teacherIds = [...new Set(
        updatedPeriods
          .filter(p => !p.isBreak && p.teacherId)
          .map(p => p.teacherId)
      )];

      if (teacherIds.length === 0) {
        console.log('No teachers to update schedules for');
        return;
      }

      // For each teacher, we need to:
      // 1. Get their existing schedule for this day
      // 2. Remove periods for this class
      // 3. Add updated periods for this class
      // 4. Save the updated schedule

      const batch = writeBatch(db);

      for (const teacherId of teacherIds) {
        const scheduleDocId = `${teacherId}_${day}`;
        const scheduleRef = doc(db, `school-users/${domain}/teacher-schedules`, scheduleDocId);

        // Get existing schedule
        const existingDoc = await getDoc(scheduleRef);
        let existingPeriods: any[] = [];

        if (existingDoc.exists()) {
          const existingData = existingDoc.data();
          existingPeriods = existingData.periods || [];
          // Remove periods for this class (we'll add updated ones)
          existingPeriods = existingPeriods.filter(
            (p: any) => p.classId !== timetableClassId
          );
        }

        // Get this teacher's periods from the updated timetable
        const teacherPeriods = updatedPeriods
          .filter(p => p.teacherId === teacherId && !p.isBreak)
          .map(p => ({
            ...p,
            classId: timetableClassId,
            className: timetableClassName,
          }));

        // Combine existing periods (from other classes) with updated periods
        const allPeriods = [...existingPeriods, ...teacherPeriods];

        // Sort by start time
        allPeriods.sort((a, b) => {
          const timeA = a.startTime?.split(':').map(Number) || [0, 0];
          const timeB = b.startTime?.split(':').map(Number) || [0, 0];
          return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
        });

        // Find teacher name from periods or teachers list
        const teacherName = teacherPeriods[0]?.teacherName ||
          teachers.find(t => t.teacherId === teacherId)?.name ||
          'Unknown';

        const scheduleData = {
          teacherId,
          teacherName,
          day,
          date: timetableDate,
          periods: allPeriods,
          lastUpdated: Timestamp.now(),
          ...(existingDoc.exists() ? {} : { createdAt: Timestamp.now() }),
        };

        batch.set(scheduleRef, scheduleData, { merge: true });
      }

      // Also handle teachers who were REMOVED from the timetable
      // by checking old periods vs new periods
      if (timetable?.periods) {
        const oldTeacherIds = [...new Set(
          timetable.periods
            .filter(p => !p.isBreak && p.teacherId)
            .map(p => p.teacherId)
        )];

        const removedTeacherIds = oldTeacherIds.filter(id => !teacherIds.includes(id));

        for (const teacherId of removedTeacherIds) {
          const scheduleDocId = `${teacherId}_${day}`;
          const scheduleRef = doc(db, `school-users/${domain}/teacher-schedules`, scheduleDocId);

          const existingDoc = await getDoc(scheduleRef);
          if (existingDoc.exists()) {
            const existingData = existingDoc.data();
            let existingPeriods = existingData.periods || [];

            // Remove periods for this class
            existingPeriods = existingPeriods.filter(
              (p: any) => p.classId !== timetableClassId
            );

            if (existingPeriods.length > 0) {
              batch.update(scheduleRef, {
                periods: existingPeriods,
                lastUpdated: Timestamp.now(),
              });
            } else {
              // No more periods for this day, could delete the document
              // but we'll keep it with empty periods for now
              batch.update(scheduleRef, {
                periods: [],
                lastUpdated: Timestamp.now(),
              });
            }
          }
        }
      }

      await batch.commit();
      console.log('✅ Teacher schedules updated successfully');
    } catch (error) {
      console.error('❌ Error updating teacher schedules:', error);
      throw error;
    }
  };

  // Helper function to find matching teacher from the teachers list
  // This handles cases where teacherId in timetable might not exactly match teachers list
  const findMatchingTeacher = (periodTeacherId: string, periodTeacherName: string): string => {
    // First, try exact match by teacherId
    const exactMatch = teachers.find(t => t.teacherId === periodTeacherId);
    if (exactMatch) {
      return exactMatch.teacherId;
    }

    // If no exact match, try matching by teacher name
    if (periodTeacherName) {
      const nameMatch = teachers.find(
        t => t.name.toLowerCase() === periodTeacherName.toLowerCase()
      );
      if (nameMatch) {
        console.log(`📌 Teacher ID mismatch fixed: "${periodTeacherId}" → "${nameMatch.teacherId}" (matched by name: ${periodTeacherName})`);
        return nameMatch.teacherId;
      }
    }

    // Return original teacherId if no match found (will show "Select Teacher")
    return periodTeacherId;
  };

  // Normalize periods to match teachers list
  const normalizePeriodsWithTeachers = (periods: Period[]): Period[] => {
    return periods.map(period => {
      if (period.isBreak || !period.teacherId) {
        return period;
      }

      const matchedTeacherId = findMatchingTeacher(period.teacherId, period.teacherName);
      const matchedTeacher = teachers.find(t => t.teacherId === matchedTeacherId);

      return {
        ...period,
        teacherId: matchedTeacherId,
        teacherName: matchedTeacher?.name || period.teacherName,
      };
    });
  };

  const loadTimetable = async () => {
    if (!classId || !section || !selectedDay) {
      alert('Please enter Class, Section, and select a Day');
      return;
    }

    setLoading(true);
    try {
      const timetableId = `class${classId.toLowerCase()}${section.toLowerCase()}_${selectedDay.toLowerCase()}`;
      const timetableRef = doc(db, `school-users/${domain}/timetables`, timetableId);
      const timetableDoc = await getDoc(timetableRef);

      if (timetableDoc.exists()) {
        const data = timetableDoc.data() as DayTimetable;
        setTimetable(data);

        // Normalize periods to match teachers list for proper dropdown display
        const normalizedPeriods = normalizePeriodsWithTeachers(
          JSON.parse(JSON.stringify(data.periods))
        );
        setEditedPeriods(normalizedPeriods);

        console.log('📅 Loaded timetable with periods:', normalizedPeriods.map(p => ({
          subject: p.subject,
          teacherId: p.teacherId,
          teacherName: p.teacherName
        })));

        setHasChanges(false);
      } else {
        alert('Timetable not found for this class, section, and day');
        setTimetable(null);
        setEditedPeriods([]);
      }
    } catch (error) {
      console.error('Error loading timetable:', error);
      alert('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (index: number, field: keyof Period, value: string | boolean | number) => {
    const updated = [...editedPeriods];

    if (field === 'isBreak') {
      updated[index].isBreak = value as boolean;
      if (value) {
        updated[index].subject = 'BREAK';
        updated[index].teacherId = '';
        updated[index].teacherName = '';
      }
    } else if (field === 'teacherId') {
      // When teacher is selected, update both teacherId and teacherName
      const selectedTeacher = teachers.find(t => t.teacherId === value);
      if (selectedTeacher) {
        updated[index].teacherId = selectedTeacher.teacherId;
        updated[index].teacherName = selectedTeacher.name;
      } else {
        updated[index].teacherId = value as string;
      }
    } else if (field === 'order' || field === 'duration') {
      updated[index][field] = value as number;
    } else {
      // For string fields: periodId, periodNumber, subject, teacherName, startTime, endTime, type
      (updated[index][field] as string) = value as string;
    }

    // Recalculate duration if time changes
    if (field === 'startTime' || field === 'endTime') {
      updated[index].duration = calculateDuration(
        updated[index].startTime,
        updated[index].endTime
      );
    }

    setEditedPeriods(updated);
    setHasChanges(true);
  };

  const addPeriod = () => {
    const newPeriod: Period = {
      periodId: `period_${editedPeriods.length + 1}`,
      periodNumber: `Period ${editedPeriods.length + 1}`,
      order: editedPeriods.length + 1,
      subject: '',
      teacherId: '',
      teacherName: '',
      startTime: '',
      endTime: '',
      duration: 0,
      isBreak: false,
      type: 'Regular',
    };
    setEditedPeriods([...editedPeriods, newPeriod]);
    setHasChanges(true);
  };

  const removePeriod = (index: number) => {
    if (confirm('Are you sure you want to remove this period?')) {
      const updated = editedPeriods.filter((_, i) => i !== index);
      // Re-number remaining periods
      updated.forEach((period, i) => {
        period.order = i + 1;
        period.periodNumber = `Period ${i + 1}`;
        period.periodId = `period_${i + 1}`;
      });
      setEditedPeriods(updated);
      setHasChanges(true);
    }
  };

  const saveTimetable = async () => {
    if (!timetable || !hasChanges) return;

    // Validate periods
    for (let i = 0; i < editedPeriods.length; i++) {
      const period = editedPeriods[i];
      if (!period.subject) {
        alert(`Period ${i + 1}: Subject is required`);
        return;
      }
      if (!period.isBreak && (!period.startTime || !period.endTime)) {
        alert(`Period ${i + 1}: Start time and end time are required`);
        return;
      }
    }

    setSaving(true);
    try {
      const timetableId = `class${classId.toLowerCase()}${section.toLowerCase()}_${selectedDay.toLowerCase()}`;
      const timetableRef = doc(db, `school-users/${domain}/timetables`, timetableId);

      const updatedData = {
        ...timetable,
        periods: editedPeriods,
        updatedAt: Timestamp.now(),
        updatedBy: 'admin_modification',
      };

      // Update timetable
      await updateDoc(timetableRef, updatedData);

      // Update teacher schedules for all affected teachers
      await updateTeacherSchedules(
        editedPeriods,
        selectedDay,
        timetable.classId,
        timetable.className,
        timetable.date || new Date().toISOString().split('T')[0]
      );

      setTimetable(updatedData);
      setHasChanges(false);
      alert('Timetable and teacher schedules updated successfully!');
    } catch (error) {
      console.error('Error saving timetable:', error);
      alert('Failed to save timetable. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    if (confirm('Are you sure you want to discard all changes?')) {
      if (timetable) {
        setEditedPeriods(JSON.parse(JSON.stringify(timetable.periods)));
        setHasChanges(false);
      }
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-pencil-square me-2 text-primary-custom"></i>
          Modify Timetable
        </h5>
        <small className="text-muted">Edit existing class timetables</small>
      </div>
      <div className="card-body">
        {/* Search Section */}
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <label className="form-label">
              <i className="bi bi-bookmark me-1"></i>
              Class
            </label>
            <select
              className="form-select"
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select Class</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>
                  Class {cls}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">
              <i className="bi bi-columns me-1"></i>
              Section
            </label>
            <select
              className="form-select"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              disabled={loading}
            >
              <option value="">Select Section</option>
              {sections.map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">
              <i className="bi bi-calendar-day me-1"></i>
              Day
            </label>
            <select
              className="form-select"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              disabled={loading}
            >
              {days.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">&nbsp;</label>
            <button
              className="btn btn-primary w-100"
              onClick={loadTimetable}
              disabled={loading || !classId || !section}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2"></i>
                  Load Timetable
                </>
              )}
            </button>
          </div>
        </div>

        {/* Timetable Info */}
        {timetable && (
          <div className="alert alert-info mb-4">
            <div className="row">
              <div className="col-md-3">
                <strong>Class:</strong> {timetable.className}
              </div>
              <div className="col-md-3">
                <strong>Day:</strong> {timetable.day}
              </div>
              <div className="col-md-3">
                <strong>Academic Year:</strong> {timetable.academicYear}
              </div>
              <div className="col-md-3">
                <strong>Periods:</strong> {editedPeriods.length}
              </div>
            </div>
          </div>
        )}

        {/* Changes Indicator */}
        {hasChanges && (
          <div className="alert alert-warning mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>You have unsaved changes!</strong> Remember to save your modifications.
          </div>
        )}

        {/* Periods Editor */}
        {timetable && editedPeriods.length > 0 && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Edit Periods
              </h6>
              <button className="btn btn-sm btn-success" onClick={addPeriod}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Period
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '50px' }}>#</th>
                    <th style={{ width: '150px' }}>Subject</th>
                    <th style={{ width: '150px' }}>Teacher Name</th>
                    <th style={{ width: '120px' }}>Teacher ID</th>
                    <th style={{ width: '100px' }}>Start Time</th>
                    <th style={{ width: '100px' }}>End Time</th>
                    <th style={{ width: '80px' }}>Duration</th>
                    <th style={{ width: '100px' }}>Type</th>
                    <th style={{ width: '80px' }}>Break</th>
                    <th style={{ width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {editedPeriods.map((period, index) => (
                    <tr key={period.periodId}>
                      <td className="text-center">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={period.subject}
                          onChange={(e) => handlePeriodChange(index, 'subject', e.target.value)}
                          disabled={period.isBreak}
                        />
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={period.teacherId}
                          onChange={(e) => handlePeriodChange(index, 'teacherId', e.target.value)}
                          disabled={period.isBreak}
                        >
                          <option value="">Select Teacher</option>
                          {teachers.map((teacher) => (
                            <option key={teacher.teacherId} value={teacher.teacherId}>
                              {teacher.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={period.teacherId}
                          disabled
                          style={{ backgroundColor: '#e9ecef' }}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={period.startTime}
                          onChange={(e) => handlePeriodChange(index, 'startTime', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={period.endTime}
                          onChange={(e) => handlePeriodChange(index, 'endTime', e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <span className="badge bg-info">{period.duration}m</span>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={period.type}
                          onChange={(e) => handlePeriodChange(index, 'type', e.target.value)}
                        >
                          <option value="Regular">Regular</option>
                          <option value="Lab">Lab</option>
                          <option value="Sports">Sports</option>
                          <option value="Activity">Activity</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={period.isBreak}
                          onChange={(e) => handlePeriodChange(index, 'isBreak', e.target.checked)}
                        />
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removePeriod(index)}
                          title="Remove period"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {timetable && (
          <div className="d-flex gap-2 justify-content-end">
            <button
              className="btn btn-secondary"
              onClick={discardChanges}
              disabled={!hasChanges || saving}
            >
              <i className="bi bi-x-circle me-2"></i>
              Discard Changes
            </button>
            <button
              className="btn btn-success"
              onClick={saveTimetable}
              disabled={!hasChanges || saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}

        {/* No Data Message */}
        {!timetable && !loading && (
          <div className="text-center py-5">
            <i className="bi bi-calendar2-x text-muted" style={{ fontSize: '3rem' }}></i>
            <p className="text-muted mt-3">Select a class, section, and day to load the timetable</p>
          </div>
        )}

        {/* Help Section */}
        <div className="alert alert-light mt-4">
          <h6 className="alert-heading">
            <i className="bi bi-info-circle me-2"></i>
            How to Modify Timetables
          </h6>
          <ul className="small mb-0">
            <li><strong>Select Class</strong> - Choose from Class 1 to Class 12</li>
            <li><strong>Select Section</strong> - Choose from Section A to J</li>
            <li><strong>Select Day</strong> - Choose which day's timetable to modify</li>
            <li><strong>Load Timetable</strong> - Click to fetch existing timetable data</li>
            <li><strong>Edit Fields</strong> - Click on any field in the table to edit directly</li>
            <li><strong>Add Period</strong> - Use the "+ Add Period" button to add new periods at the end</li>
            <li><strong>Remove Period</strong> - Click the trash icon (🗑️) to remove unwanted periods</li>
            <li><strong>Mark Breaks</strong> - Check the "Break" checkbox to mark a period as break time</li>
            <li><strong>Time Management</strong> - Duration is calculated automatically when you set start and end times</li>
            <li><strong>Save Changes</strong> - Click "Save Changes" when you're done editing (teacher schedules will auto-update!)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ModifyTimeTable;
